import { useMemo } from "react";
import type Customer from "@acto/taskpane/core/domain/Customer";
import {
  type RevenuePeriod,
  useRankedClientsWithKpis,
} from "@acto/taskpane/core/data-access/customers/useRankedClientsWithKpis";
import { ClientTrendFilter } from "@acto/gql/graphql";
import { type QuickFilterState } from "./quickFilter";

interface Args {
  search: string;
  quickFilter: QuickFilterState;
  pageSize?: number;
}

interface Result {
  customers: Customer[] | undefined;
  /** Backend-resolved KPI window; source of truth for the card's period label. */
  revenuePeriod: RevenuePeriod | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
}

const DEFAULT_PAGE_SIZE = 10;

export function useHomeCustomerList({ search, quickFilter, pageSize = DEFAULT_PAGE_SIZE }: Args): Result {
  // `rankedClients` covers both modes: with no search term the server returns
  // today's prioritised batch first, then ranker-score fill; with a search
  // term it returns smart-search-ranked results.
  // Trends filter server-side across ALL customers, so they combine with a
  // search term. Focus (recommendedOnly) narrows to today's recommendation
  // batch and stays off while searching, so a search reaches every customer.
  const isSearching = search.length > 0;
  const serverFocus = !isSearching && quickFilter.focus;
  const serverTrends = useMemo<ClientTrendFilter[] | undefined>(() => {
    const trends: ClientTrendFilter[] = [];
    if (quickFilter.growth) trends.push(ClientTrendFilter.Growth);
    if (quickFilter.decline) trends.push(ClientTrendFilter.Decline);
    return trends.length > 0 ? trends : undefined;
  }, [quickFilter.growth, quickFilter.decline]);

  const result = useRankedClientsWithKpis({
    limit: pageSize,
    nameStartsWithOrExternalReference: isSearching ? search : undefined,
    focusOnly: serverFocus || undefined,
    trends: serverTrends,
  });

  const hasNoRowsToShow = result.data == null || result.data.length === 0;
  return {
    customers: result.data,
    revenuePeriod: result.revenuePeriod,
    isLoading: hasNoRowsToShow && (result.isPending || result.isFetching),
    isLoadingMore: result.isFetching && !hasNoRowsToShow,
    hasMore: result.hasNextPage,
    loadMore: result.fetchNextPage,
  };
}
